import { computed, effect } from '@preact/signals';
import { round } from 'es-toolkit';
import toast from 'react-hot-toast';
import { Episode } from '../models/podcasts';
import AndroidAudioPlayer from '../utils/android-audio-player';
import { setupAudioPlayingLoop } from '../utils/audio-loop';
import debugLog from '../utils/debug';
import {
  checkEpisodeAfterFailure,
  reportSentryError,
  reportSentryWarning,
  showMessageWhenPlaybackFails,
} from '../utils/error-handling';
import { allowAdSkipOrPlayFailedToSkipAdSound } from '../utils/gate-ad-skip';
import { exposeOnWindow, isEpisodeIdentifier, secondsToTime } from '../utils/helpers';
import IOSAudioPlayer from '../utils/ios-audio-player';
import { getInitialValue, removeItemSymbol, setLazyValue } from '../utils/localstorage';
import MockAudioPlayer from '../utils/mock-audio-player';
import { persistProgress } from '../utils/persist-progress';
import {
  isAndroidNativeApp,
  isIOSNativeApp,
  isLocalDevelopment,
  isNativeAppWithStateV1,
  isPlaylistSupported,
  isWebApp,
} from '../utils/platform';
import { trackPlayEpisodeInUpNextAutomatically } from '../utils/plausible-events';
import { preloadInitialEpisode } from '../utils/preload-initial-episode';
import VanillaAudioPlayer from '../utils/vanilla-audio-player';
import {
  currentDuration,
  currentEpisode,
  currentPlaylist,
  currentSeek,
  playbackRate,
  requestedSeek,
  requestedState,
  state,
} from './audio-signals';
import { DownloadsStore } from './downloads';
import { initializeBasedOnNativeAppState } from './native-app-state';
import { initNativeUsage } from './native-usage';
import { idbStartupPromise, idToEpisode } from './podcasts';
import { trackAdSkipUnlessRecent } from './usage-data';
import {
  addToRecentEpisodes,
  bumpLastViewedEpisodeDate,
  UserDataStore,
  useUserDataStore,
} from './user-data';

const log = (...args: any[]) => {
  debugLog({ logName: 'audio-state', logColor: 'darkblue' }, ...args);
};

//
// Setup appropriate player for environment and preload initial audio
//

const player = isIOSNativeApp
  ? new IOSAudioPlayer()
  : isAndroidNativeApp
    ? new AndroidAudioPlayer()
    : typeof window !== 'undefined'
      ? new VanillaAudioPlayer()
      : new MockAudioPlayer();

if (typeof window !== 'undefined') {
  initNativeUsage({
    acknowledgeAdSkipsUpTo: (timestampMs) => player.acknowledgeAdSkipsUpTo(timestampMs),
    acknowledgeUsageData: (payload) => player.acknowledgeUsageData(payload),
  });
}

//
// Expose actions that call player methods and update state
//

export function loadNewEpisode(
  episode: Episode,
  seek = 0,
  { skipAdAtStart = true } = {}
) {
  persistProgress.flush(); // Persist last episode's progress
  persistProgress.cancel(); // Persist this episode's progress as soon as it plays

  const duration = episode.duration;
  const secondsLeft = (duration || NaN) - seek;
  if (secondsLeft < 10) {
    seek = 0;
    log(`Setting seek to beginning since ${round(secondsLeft)}s from end`);
  }

  // Skip initial ad if user has ad-skip credits
  let playSkipAdSound = false;
  if (
    skipAdAtStart &&
    seek < 1 &&
    episode.ads &&
    episode.ads[0] &&
    episode.ads[0].startTime < 1 &&
    allowAdSkipOrPlayFailedToSkipAdSound(player)
  ) {
    const initialAd = episode.ads[0];
    seek = initialAd.endTime;
    log('Skipping ad at start of new episode', secondsToTime(seek));
    playSkipAdSound = true;
    trackAdSkipUnlessRecent(episode.id, initialAd.startTime, initialAd.endTime);
  }

  log(
    'Load new episode',
    episode.id,
    episode.title,
    `Seek ${secondsToTime(seek)}`,
    `Duration ${duration ? secondsToTime(duration) : '?'}`,
    (episode.ads || []).map(
      (ad) => `${secondsToTime(ad.startTime)} - ${secondsToTime(ad.endTime)}`
    ),
    episode.audioUrl
  );

  // Update state
  state.value = 'loading';
  requestedState.value = 'paused';
  currentSeek.value = seek;
  currentEpisode.value = episode;
  currentDuration.value = NaN;

  // Remove episode from playlist (if supported)
  if (isPlaylistSupported && currentPlaylist.value.some(([id]) => id === episode.id)) {
    currentPlaylist.value = currentPlaylist.value.filter(([id]) => id !== episode.id);
  }

  player.loadNewEpisode(episode, seek, playSkipAdSound);
}

export function reset() {
  requestedState.value = 'unloaded';
  player.reset();
}

export function play() {
  requestedState.value = 'playing';
  player.play();

  // Add to recent episodes IDs
  const episode = currentEpisode.peek() as Episode;
  const compoundId = `${episode.podcastId}|${episode.guidHash}`;
  const { recentEpisodeIds } = useUserDataStore.getState();
  if (recentEpisodeIds[0] !== compoundId) {
    addToRecentEpisodes(compoundId);
  }

  // Update podcastsLastViewedEpisodeDate for this podcast
  bumpLastViewedEpisodeDate(episode);
}

export function pause() {
  requestedState.value = 'paused';
  player.pause();
}

export function seekTo(seconds: number, reason = '') {
  const requestedSeconds = seconds;

  // Should never happen, but avoid breaking things if it does
  if (Number.isNaN(seconds)) return reportSentryError(new Error('NaN seek'));
  if (seconds === Infinity) return reportSentryError(new Error('Infinity seek'));

  // Avoid negative seek
  if (seconds < 0) seconds = 0;

  // Use if-statement instead of clamp to handle NaN currentDuration
  if (seconds > currentDuration.value) seconds = currentDuration.value;

  if (requestedSeconds !== seconds)
    log(`seekTo() coerced invalid requested seconds: ${requestedSeconds}`);

  log('seekTo()', secondsToTime(seconds, 2), reason);

  requestedSeek.value = seconds;

  const stateBeforeSeek = state.value;
  state.value = 'seeking';

  // Update requestedState to indicate value after seeking
  // Probably not necessary for the audio to resume the correct state,
  // but ensures the data model doesn't get out of sync
  if (!requestedState.value) {
    requestedState.value = stateBeforeSeek === 'playing' ? 'playing' : 'paused';
  }

  player.seekTo(seconds, reason === 'skip ad');
}

effect(() => {
  player.updatePlaybackRate(playbackRate.value);
});

//
// When episode fails to play, make sure it's been uploaded. If not,
// tell the user so they can try again in a little bit
//

effect(() => {
  const isErrorState = state.value === 'error'; // Only trigger when entering error state
  const episode = currentEpisode.peek();
  if (isErrorState && episode) {
    checkEpisodeAfterFailure(episode, 'play_episode').then(({ isSaved, status }) =>
      showMessageWhenPlaybackFails(isSaved, status, 'play')
    );
  }
});

if (typeof window !== 'undefined' && !isNativeAppWithStateV1) {
  // Deprecated: setup async loop to update currentSeek and skip ads
  // Newer versions of native apps (currently just Android) skip ads natively and also
  // trigger seek updates automatically
  setupAudioPlayingLoop(player, seekTo);
}

// Get logs from native app
export async function getNativeLogs() {
  return 'getNativeLogs' in player ? player.getNativeLogs() : undefined;
}

//
// Handle browser events for currently playing audio
//

if (
  typeof window !== 'undefined' &&
  !isIOSNativeApp &&
  !isAndroidNativeApp &&
  navigator.mediaSession
) {
  navigator.mediaSession.setActionHandler('play', () => {
    play();
  });
  navigator.mediaSession.setActionHandler('pause', () => {
    pause();
  });
  navigator.mediaSession.setActionHandler('seekbackward', ({ seekOffset }) => {
    seekOffset = seekOffset || 10;
    seekTo(currentSeek.value - seekOffset, `ms seekbackward ${seekOffset}`);
  });
  navigator.mediaSession.setActionHandler('seekforward', ({ seekOffset = 30 }) => {
    seekOffset = seekOffset || 30;
    seekTo(currentSeek.value + seekOffset, `ms seekforward ${seekOffset}`);
  });
  computed(() => {
    if (['playing', 'paused'].includes(state.value)) {
      navigator.mediaSession.playbackState = state.value as 'playing' | 'paused';
    }
  });
}

//
// Handle downloading audio and removing audio downloads
//

export async function downloadAudioFile(episode: Episode) {
  return player.downloadAudioFile(episode);
}

export async function deleteAudioFile(episode: Episode) {
  return player.deleteAudioFile(episode);
}

export async function checkDownloadedFiles(
  getDownloadsState: () => DownloadsStore,
  getUserDataState: () => UserDataStore
) {
  player.checkDownloadedFiles(getDownloadsState, getUserDataState);
}

//
// Handle playing next episode when previous one ends (only used by web app)
//
if (isWebApp && isPlaylistSupported) {
  addEventListener('EPISODE_ENDED_START_NEXT_ONE', () => {
    const [nextEpisodeId, startingPostion] = currentPlaylist.value[0] || [];
    if (nextEpisodeId) {
      const episode = idToEpisode(nextEpisodeId);
      if (episode) {
        log('Playing next episode in playlist automatically', episode.id);
        loadNewEpisode(episode, startingPostion, { skipAdAtStart: true });
        play();
        trackPlayEpisodeInUpNextAutomatically(episode);
      } else {
        reportSentryWarning(
          new Error(`Next playlist episode ${nextEpisodeId} not found`)
        );
      }
    }
  });
}

//
// Gross this shouldn't go here. Refactor eventually...
//

export async function restorePurchase() {
  if (player instanceof IOSAudioPlayer) player.restorePurchase();
}

export async function makeNativePurchase(
  stripeLookupKey: 'alpha_user_monthly_3.99' | '100_forever_ad_skips_for_5.99'
) {
  // Translate Stripe lookup key into corresponding native app purchase managed
  // by RevenueCat
  let offeringId;
  let packageId;
  if (stripeLookupKey === 'alpha_user_monthly_3.99') {
    offeringId = isLocalDevelopment ? 'paid_plan_v0_test' : 'paid_plan_v0';
    packageId = '$rc_monthly';
  } else if (stripeLookupKey === '100_forever_ad_skips_for_5.99') {
    offeringId = isLocalDevelopment ? '100_ad_skips_v0_test' : '100_ad_skips_v0';
    packageId = '$rc_lifetime';
  } else {
    toast.error("Sorry, this purchase isn't available in app");
    return reportSentryError(
      new Error(`Invalid lookup key for purchase: ${stripeLookupKey}`)
    );
  }

  if ('makePurchase' in player) {
    return player.makePurchase(offeringId, packageId);
  }
  throw new Error('makePurchase missing on player');
}

//
// Get / set current episode ID in LocalStorage
//

// Wait until IndexedDB has been initialized before setting current episode so we can display its metadata
// This is typically < 1s so it's fine to wait. Keeps everything simpler.
idbStartupPromise.promise.then(() => {
  if (
    initializeBasedOnNativeAppState((episode, seek) =>
      loadNewEpisode(episode, seek, { skipAdAtStart: false })
    )
  ) {
    log('Set current episode from native app state', currentEpisode.value?.id);
  } else {
    log('Setting current episode from LocalStorage');
    // Set current episode from LocalStorage
    const id = getInitialValue('currentEpisodeId', isEpisodeIdentifier);
    currentEpisode.value = id ? window.globalPodcastActions.idToEpisode(id) : undefined;

    // Old: use seekFromLastSession to use more accurate seek when setting up initial episode
    preloadInitialEpisode((episode, seek) =>
      loadNewEpisode(episode, seek, { skipAdAtStart: false })
    );
  }

  // Update LocalStorage every time episode changes
  effect(() => {
    const episode = currentEpisode.value;
    setLazyValue('currentEpisodeId', episode ? episode.id : removeItemSymbol);
  });
});

// // @ts-ignore
// window.downloadHelpers = {
//   downloadAudioFile,
//   deleteAudioFile,
//   checkDownloadedFiles,
// };

exposeOnWindow({
  getNativeLogs,
  player,
});
